import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, Pencil, Trash2, Plus, ArrowLeft, Minus } from "lucide-react";
import { ConfigField } from "config/adminConfig";
import { validateConfigValue } from "config/adminConfigLimits";
import adminConfigService, { ConfigItem, isSeededDefault } from "services/AdminConfig/adminConfigService";
import HeaderButtons from "components/Button/HeaderButtons";
import { Spinner, fieldCls } from "components/InlineTable";
import GenericModal from "components/Modal";
import DeletePopup from "components/DeletePopup/deletePopup";
// NOTE: shared resolver so the API's own reason reaches the user instead of a
// generic "Failed to …" — the catch blocks here used to swallow the error.
import { getApiErrorMessage } from "util/errorMessage";
import { useToaster } from "contexts/toasterContext";

// Generic name/id CRUD editor. Rendered for every config field except those
// with a custom `variant`. When `embedded` is true it renders without the page
// header/back button (used as a tab inside the module page).
export const GenericConfigDetail: React.FC<{ field: ConfigField; embedded?: boolean }> = ({
  field,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const { addToast } = useToaster();

  const [items, setItems] = useState<ConfigItem[]>([]);
  const [search, setSearch] = useState("");
  const [pageLoading, setPageLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  const [newNames, setNewNames] = useState<string[]>([]);
  const [newNameErrors, setNewNameErrors] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editError, setEditError] = useState("");
  const [updating, setUpdating] = useState<number | null>(null);

  const [deleting, setDeleting] = useState<number | null>(null);

  // Item pending delete confirmation (drives the confirm modal).
  const [deleteTarget, setDeleteTarget] = useState<ConfigItem | null>(null);

  const editInputRef = useRef<HTMLInputElement>(null);
  const lastNewInputRef = useRef<HTMLInputElement>(null);

  const isAnyBusy = adding || !!deleting || !!updating || pageLoading;

  useEffect(() => {
    if (newNames.length > 0) {
      lastNewInputRef.current?.focus();
      lastNewInputRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [newNames.length]);

  useEffect(() => {
    if (!field) return;
    setPageLoading(true);
    adminConfigService
      .getAll(field.apiEndpoint)
      .then((res) => setItems(res.data))
      // A load failure has no action behind it, so it stays an inline banner
      // rather than a toast — the list is empty and the message must persist.
      .catch((err) => setApiError(getApiErrorMessage(err, "Failed to load values.")))
      .finally(() => setPageLoading(false));
  }, [field?.apiEndpoint]); // eslint-disable-line

  useEffect(() => {
    if (editingId !== null) editInputRef.current?.focus();
  }, [editingId]);

  // ── Inline edit ───────────────────────────────────────────────────────────
  const startEdit = (item: ConfigItem) => {
    // CHANGE: seeded system defaults are protected from EDIT as well as delete.
    // Renaming a seeded value breaks the records that reference it by name, so
    // the guard lives here too — not just on the disabled button — to cover any
    // other path into edit mode (keyboard, future row click).
    if (isAnyBusy || isSeededDefault(item)) return;
    setEditingId(item.id);
    setEditingName(item.name);
    setEditError("");
    setApiError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
    setEditError("");
  };

  const commitEdit = async () => {
    const name = editingName.trim();
    const validationError = validateConfigValue(editingName, field.maxLength);
    if (validationError) { setEditError(validationError); return; }
    if (editingId === null) return;
    setUpdating(editingId);
    setApiError("");
    try {
      const res = await adminConfigService.update(field.apiEndpoint, editingId, name, {
        useNamesArray: field.updateWithNamesArray,
      });
      // Prefer the server's returned item; fall back to the known new value so the
      // row updates even if an endpoint returns no/!{id,name} body on update.
      const updated: ConfigItem =
        res?.data && typeof res.data === "object" && "id" in res.data
          ? res.data
          : { id: editingId, name };
      setItems((prev) => prev.map((i) => (i.id === editingId ? updated : i)));
      setEditingId(null);
      addToast(`${field.label} updated successfully`, "success");
    } catch (err) {
      // CHANGE: the error was swallowed and replaced with a fixed string, hiding
      // the real reason (e.g. "Name already exists"). It is now resolved from
      // the response and surfaced as a toast.
      addToast(getApiErrorMessage(err, `Failed to update ${field.label.toLowerCase()}`), "error");
    } finally {
      setUpdating(null);
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") commitEdit();
    if (e.key === "Escape") cancelEdit();
  };

  // ── Add ───────────────────────────────────────────────────────────────────
  const updateNewName = (index: number, value: string) => {
    setNewNames((prev) => prev.map((n, i) => (i === index ? value : n)));
    setNewNameErrors((prev) => prev.map((e, i) => (i === index ? "" : e)));
  };

  const addRow = () => {
    if (isAnyBusy || !!editingId) return;
    if (newNames.length > 0 && !newNames[newNames.length - 1].trim()) {
      setNewNameErrors((prev) =>
        prev.map((e, i) => (i === prev.length - 1 ? "Name is required." : e))
      );
      return;
    }
    setNewNames((prev) => [...prev, ""]);
    setNewNameErrors((prev) => [...prev, ""]);
  };

  const removeRow = (index: number) => {
    setNewNames((prev) => prev.filter((_, i) => i !== index));
    setNewNameErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const discardPending = () => {
    setNewNames([]);
    setNewNameErrors([]);
  };

  const handleAdd = async () => {
    const trimmed = newNames.map((n) => n.trim());
    const errors = trimmed.map((n) => validateConfigValue(n, field.maxLength));
    if (errors.some(Boolean)) { setNewNameErrors(errors); return; }
    setAdding(true);
    setNewNameErrors(trimmed.map(() => ""));
    setApiError("");
    try {
      const res = await adminConfigService.add(field.apiEndpoint, trimmed);
      setItems((prev) => [...prev, ...res.data]);
      setNewNames([]);
      setNewNameErrors([]);
      addToast(
        trimmed.length > 1
          ? `${trimmed.length} ${field.label} values added successfully`
          : `${field.label} added successfully`,
        "success",
      );
    } catch (err) {
      addToast(getApiErrorMessage(err, `Failed to add ${field.label.toLowerCase()}`), "error");
    } finally {
      setAdding(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (id: number) => {
    setDeleting(id);
    setApiError("");
    try {
      await adminConfigService.remove(field.apiEndpoint, id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      addToast(`${field.label} deleted successfully`, "success");
    } catch (err) {
      // A delete is commonly rejected because the value is still referenced;
      // that reason is worth showing verbatim.
      addToast(getApiErrorMessage(err, `Failed to delete ${field.label.toLowerCase()}`), "error");
    } finally {
      setDeleting(null);
    }
  };

  const lastRowFilled = newNames.length === 0 || !!newNames[newNames.length - 1].trim();

  // Check if there are any typed changes in the new rows
  const hasNewChanges = newNames.some((n) => n.trim() !== "");

  // Client-side search over the saved values (names are the only field).
  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
  }, [items, search]);

  // Reusable "Add" button — shown in the standalone header and the embedded toolbar.
  const addButton = (
    <HeaderButtons
      size="small"
      onClick={addRow}
      disabled={isAnyBusy || !!editingId || !lastRowFilled}
      icon={<Plus size={13} />}
    >
      Add {field.label}
    </HeaderButtons>
  );

  // Search box — lets admins filter long value lists.
  const searchBox = (
    <div className="relative flex-1 min-w-0 max-w-sm">

      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={`Search ${field.label.toLowerCase()}…`}
        aria-label={`Search ${field.label}`}
        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 pl-9 pr-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );

  // Compact action button styles shared across rows.
  const iconBtn =
    "inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const textBtn =
    "inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className="flex flex-col">
      {/* ── Standalone page header (hidden when embedded in the module page) ── */}
      {!embedded && (
        <div className="flex items-center gap-2.5 min-w-0 pt-2 pb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white truncate">
            {field.label}
          </h1>
        </div>
      )}

      {/* ── Toolbar: search + add ──────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 pb-3">
        {searchBox}
        {addButton}
      </div>

      {/* Surface API errors (add/edit/delete/load failures) to the user. */}
      {apiError && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-error text-sm font-medium">
          {apiError}
        </div>
      )}

      {/* List — compact, natural height; a tall list scrolls internally. */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        {pageLoading ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3.5">
                <div className="h-3.5 w-40 rounded bg-gray-100 dark:bg-gray-700 animate-pulse" />
                <div className="h-3.5 w-24 rounded bg-gray-100 dark:bg-gray-700 animate-pulse" />
              </div>
            ))}
          </div>
        ) : visibleItems.length === 0 && newNames.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 py-12 px-4 text-center">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {search.trim() ? "No matches" : `No ${field.label.toLowerCase()} yet`}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {search.trim()
                ? `Nothing matches "${search.trim()}".`
                : `Click "Add ${field.label}" to create the first one.`}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[calc(100vh-340px)] overflow-y-auto">
            {/* ── Saved rows ─────────────────────────────────────────── */}
            {visibleItems.map((item) => {
              const isEditing = editingId === item.id;
              const isUpdating = updating === item.id;
              const isDeleting = deleting === item.id;
              const otherBusy = isAnyBusy && !isEditing && !isUpdating && !isDeleting;
              const isEditDirty = isEditing && editingName !== item.name;
              // Seeded system defaults are protected from deletion.
              const isDefault = isSeededDefault(item);

              return (
                <li
                  key={item.id}
                  className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${isEditing
                    ? "bg-blue-50/60 dark:bg-blue-900/10"
                    : otherBusy
                      ? "opacity-40 pointer-events-none"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    }`}
                >
                  {isEditing ? (
                    <div className="flex-1 min-w-0">
                      <div className="relative">
                        <input
                          ref={editInputRef}
                          type="text"
                          value={editingName}
                          maxLength={field.maxLength}
                          onChange={(e) => { setEditingName(e.target.value); setEditError(""); }}
                          onKeyDown={handleEditKeyDown}
                          disabled={isUpdating}
                          className={`${fieldCls(!!editError)}${field.maxLength ? " pr-14" : ""}`}
                        />
                        {field.maxLength && (
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] tabular-nums text-gray-400 dark:text-gray-500 pointer-events-none">
                            {editingName.length}/{field.maxLength}
                          </span>
                        )}
                      </div>
                      {editError && (
                        <p className="mt-1 text-xs font-medium leading-tight text-error">{editError}</p>
                      )}
                    </div>
                  ) : (
                    <span className="flex-1 min-w-0 flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100 capitalize truncate">
                        {item.name}
                      </span>
                      {isDefault && (
                        <span
                          className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                          title="Seeded system value — can't be edited or deleted"
                        >
                          Default
                        </span>
                      )}
                    </span>
                  )}

                  <div className="flex items-center gap-1 shrink-0">
                    {isEditing ? (
                      <>
                        <button
                          onClick={commitEdit}
                          disabled={isUpdating || !isEditDirty}
                          className={`${iconBtn} bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white`}
                          aria-label="Save"
                        >
                          {isUpdating ? <Spinner className="h-3.5 w-3.5 text-white" /> : <Check size={14} />}
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={isUpdating}
                          className={`${iconBtn} border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700`}
                          aria-label="Cancel"
                        >
                          <X size={14} />
                        </button>
                      </>
                    ) : (
                      <>
                        {/* CHANGE: Edit is now disabled for seeded defaults, to
                            match Delete — renaming a seeded value would orphan
                            the records that reference it. */}
                        <button
                          onClick={() => startEdit(item)}
                          disabled={isAnyBusy || isDefault}
                          title={isDefault ? "Default values can't be edited" : undefined}
                          className={`${textBtn} text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20`}
                        >
                          <Pencil size={12} />
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          disabled={isAnyBusy || isDefault}
                          title={isDefault ? "Default values can't be deleted" : undefined}
                          className={`${textBtn} text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10`}
                        >
                          {isDeleting ? <Spinner className="h-3 w-3 text-red-500" /> : <Trash2 size={12} />}
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </li>
              );
            })}

            {/* ── Pending new rows ────────────────────────────────────── */}
            {newNames.map((name, idx) => (
              <li
                key={`new-${idx}`}
                className="flex items-center gap-3 px-4 py-2.5 border-l-2 border-blue-400 dark:border-blue-500 bg-blue-50/30 dark:bg-blue-900/5"
              >
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <input
                      ref={idx === newNames.length - 1 ? lastNewInputRef : undefined}
                      type="text"
                      placeholder={`Enter ${field.label.toLowerCase()} name`}
                      value={name}
                      maxLength={field.maxLength}
                      onChange={(e) => updateNewName(idx, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !isAnyBusy && !editingId && idx === newNames.length - 1) {
                          handleAdd();
                        }
                      }}
                      disabled={isAnyBusy || !!editingId}
                      className={`${fieldCls(!!newNameErrors[idx])}${field.maxLength ? " pr-14" : ""}`}
                    />
                    {field.maxLength && (
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] tabular-nums text-gray-400 dark:text-gray-500 pointer-events-none">
                        {name.length}/{field.maxLength}
                      </span>
                    )}
                  </div>
                  {newNameErrors[idx] && (
                    <p className="mt-1 text-xs font-medium text-error leading-tight">{newNameErrors[idx]}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={isAnyBusy || !!editingId}
                  className={`${iconBtn} shrink-0 border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-red-500 hover:border-red-300 hover:bg-red-50 dark:hover:bg-red-900/10`}
                  aria-label="Remove row"
                >
                  <Minus size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* ── Footer — only when there are pending rows ── */}
        {newNames.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60">
            <button
              type="button"
              onClick={discardPending}
              disabled={adding}
              className="text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Discard
            </button>
            <HeaderButtons
              onClick={handleAdd}
              loading={adding}
              disabled={isAnyBusy || !!editingId || !hasNewChanges}
              icon={<Check size={13} />}
            >
              Save {newNames.length > 1 ? `${newNames.length} Values` : "Value"}
            </HeaderButtons>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ───────────────────────────────────────────── */}
      {deleteTarget && (
        <GenericModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          header={`Delete ${field.label}`}
        >
          <DeletePopup
            selectedRow={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            handleDelete={(id) => {
              setDeleteTarget(null);
              handleDelete(id);
            }}
            message={`Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`}
          />
        </GenericModal>
      )}
    </div>
  );
};

export default GenericConfigDetail;